import { ApolloLink, Observable } from "@apollo/client";

type SnapshotEntry = {
  data: any;
  operationName: string;
  variables?: Record<string, unknown>;
};

type SnapshotPayload = {
  entries: SnapshotEntry[];
  ignoredMatchVariables?: Record<string, string[]>;
};

type SnapshotMode = "auto" | "off" | "on";

const SNAPSHOT_URL = "/contentful-snapshot/content.json";
let snapshotPromise: Promise<SnapshotPayload | null> | null = null;

function getSnapshotMode(): SnapshotMode {
  const raw = (process.env.REACT_APP_CONTENTFUL_SNAPSHOT_MODE || "auto")
    .toLowerCase()
    .trim();
  if (raw === "on" || raw === "off" || raw === "auto") {
    return raw;
  }
  return "auto";
}

async function loadSnapshot(mode: SnapshotMode): Promise<SnapshotPayload | null> {
  if (mode === "off") {
    return null;
  }

  if (!snapshotPromise) {
    snapshotPromise = fetch(SNAPSHOT_URL, { cache: "force-cache" })
      .then(async (response) => {
        if (!response.ok) {
          throw new Error(
            `Snapshot fetch failed (${response.status} ${response.statusText})`,
          );
        }
        return (await response.json()) as SnapshotPayload;
      })
      .catch((error) => {
        if (mode === "on") {
          snapshotPromise = null;
          throw error;
        }
        console.warn("[contentful-snapshot] Falling back to network:", error);
        return null;
      });
  }

  return snapshotPromise;
}

export function createContentfulSnapshotLink() {
  return new ApolloLink((operation, forward) => {
    const mode = getSnapshotMode();
    if (mode === "off") {
      return forward ? forward(operation) : null;
    }

    return new Observable((observer) => {
      let innerSubscription:
        | {
            unsubscribe: () => void;
          }
        | undefined;
      let closed = false;

      const fallbackToNetwork = (error?: unknown) => {
        if (closed) {
          return;
        }
        if (!forward) {
          observer.error(
            error ||
              new Error(
                `[contentful-snapshot] No local result for ${operation.operationName}`,
              ),
          );
          return;
        }
        innerSubscription = forward(operation).subscribe({
          complete: () => observer.complete(),
          error: (forwardError) => observer.error(forwardError),
          next: (value) => observer.next(value),
        });
      };

      void loadSnapshot(mode)
        .then((snapshot) => {
          if (closed) {
            return;
          }
          if (!snapshot) {
            fallbackToNetwork();
            return;
          }

          const entry = findSnapshotEntry(snapshot, operation);
          if (!entry) {
            if (mode === "on") {
              observer.error(
                new Error(
                  `[contentful-snapshot] Missing snapshot entry for ${operation.operationName}`,
                ),
              );
              return;
            }
            fallbackToNetwork();
            return;
          }

          observer.next({
            data: applyRuntimeTransforms({
              data: cloneData(entry.data),
              operationName: entry.operationName,
              variables: operation.variables || {},
            }),
          });
          observer.complete();
        })
        .catch((error) => {
          if (mode === "on") {
            observer.error(error);
            return;
          }
          fallbackToNetwork(error);
        });

      return () => {
        closed = true;
        innerSubscription?.unsubscribe();
      };
    });
  });
}

function findSnapshotEntry(snapshot: SnapshotPayload, operation: any) {
  const operationName = String(operation.operationName || "");
  const entries = snapshot.entries || [];
  const candidates =
    entries.filter((entry) => entry.operationName === operationName) ||
    [];
  if (candidates.length === 0) {
    return null;
  }

  const ignoredVariables = snapshot.ignoredMatchVariables || {};
  const normalizedRequestVariables = normalizeVariables(
    operation.variables || {},
    ignoredVariables[operationName] || [],
  );

  let bestMatch: SnapshotEntry | null = null;
  let bestScore = -1;

  for (const candidate of candidates) {
    const candidateVariables = candidate.variables || {};
    if (!isSubsetMatch(candidateVariables, normalizedRequestVariables)) {
      continue;
    }
    const exact = deepEqual(candidateVariables, normalizedRequestVariables);
    const score =
      (exact ? 1000 : 0) + Object.keys(candidateVariables).length;
    if (score > bestScore) {
      bestScore = score;
      bestMatch = candidate;
    }
  }

  if (bestMatch) {
    return bestMatch;
  }

  if (candidates.length === 1) {
    return candidates[0];
  }

  return null;
}

function normalizeVariables(
  variables: Record<string, unknown>,
  ignoredKeys: string[],
): Record<string, unknown> {
  const ignored = new Set(ignoredKeys);
  const output: Record<string, unknown> = {};

  for (const [key, value] of Object.entries(variables || {})) {
    if (value === undefined || ignored.has(key)) {
      continue;
    }
    output[key] = value;
  }

  return output;
}

function isSubsetMatch(
  subset: Record<string, unknown>,
  target: Record<string, unknown>,
) {
  return Object.entries(subset).every(([key, value]) => deepEqual(target[key], value));
}

function deepEqual(a: unknown, b: unknown): boolean {
  if (a === b) {
    return true;
  }

  if (Array.isArray(a) && Array.isArray(b)) {
    if (a.length !== b.length) {
      return false;
    }
    return a.every((value, index) => deepEqual(value, b[index]));
  }

  if (
    a &&
    b &&
    typeof a === "object" &&
    typeof b === "object" &&
    !Array.isArray(a) &&
    !Array.isArray(b)
  ) {
    const aEntries = Object.entries(a as Record<string, unknown>);
    const bEntries = Object.entries(b as Record<string, unknown>);
    if (aEntries.length !== bEntries.length) {
      return false;
    }
    return aEntries.every(([key, value]) => deepEqual(value, (b as any)[key]));
  }

  return false;
}

function applyRuntimeTransforms(input: {
  data: any;
  operationName: string;
  variables: Record<string, unknown>;
}) {
  const { data, operationName, variables } = input;

  if (
    operationName === "pool" &&
    typeof variables.now === "string" &&
    data?.eventoPiscinaCollection?.items
  ) {
    const minTimestamp = Date.parse(variables.now);
    if (!Number.isNaN(minTimestamp)) {
      data.eventoPiscinaCollection.items = data.eventoPiscinaCollection.items.filter(
        (item: any) => {
          const timestamp = Date.parse(item?.data || "");
          return Number.isFinite(timestamp) && timestamp >= minTimestamp;
        },
      );
    }
  }

  if (
    operationName === "articles" &&
    typeof variables.limit === "number" &&
    data?.articoloStampaCollection?.items
  ) {
    data.articoloStampaCollection.items = data.articoloStampaCollection.items.slice(
      0,
      variables.limit,
    );
  }

  return data;
}

function cloneData<T>(value: T): T {
  if (typeof structuredClone === "function") {
    return structuredClone(value);
  }
  return JSON.parse(JSON.stringify(value)) as T;
}
